// src/components/GestionarModulosModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { FaTimes, FaCheck, FaExclamationTriangle, FaBuilding, FaUsers, FaSwimmingPool, FaShieldAlt, FaDollarSign, FaCog, FaDoorOpen, FaMicrochip, FaKey } from 'react-icons/fa';
import './GestionarModulosModal.css';

// Estructura de módulos por categorías
const categoriasModulos = {
  comunidad: {
    label: 'Comunidad',
    icon: FaUsers,
    color: '#10b981',
    general: {
      key: 'comunidad',
      label: 'Pestaña Comunidad',
      description: 'Activar o desactivar toda la pestaña de Comunidad'
    },
    subcategorias: [
      {
        key: 'chat_comunitario',
        label: 'Chat Comunitario',
        description: 'Permite el chat entre residentes'
      },
      {
        key: 'asistente_virtual_ia',
        label: 'Asistente Virtual IA',
        description: 'Habilita el widget de asistencia inteligente (IA) para residentes y administradores'
      }
    ]
  },
  amenidades: {
    label: 'Amenidades',
    icon: FaSwimmingPool,
    color: '#3b82f6',
    general: {
      key: 'amenidades',
      label: 'Pestaña Amenidades',
      description: 'Activar o desactivar toda la pestaña de Amenidades'
    },
    subcategorias: []
  },
  vigilancia: {
    label: 'Vigilancia',
    icon: FaShieldAlt,
    color: '#8b5cf6',
    general: {
      key: 'vigilancia',
      label: 'Pestaña Vigilancia',
      description: 'Activar o desactivar toda la pestaña de Vigilancia'
    },
    subcategorias: [
      {
        key: 'validar_qr',
        label: 'Validar QR',
        description: 'Permite validar códigos QR de visitas'
      },
      {
        key: 'generar_qr_invitado',
        label: 'Generar QR para Invitado',
        description: 'Permite generar códigos QR para invitados'
      },
      {
        key: 'registrar_paquete',
        label: 'Registrar Paquete',
        description: 'Permite registrar paquetes recibidos'
      },
      {
        key: 'visitas_frecuentes',
        label: 'Visitas Frecuentes',
        description: 'Gestión de visitantes frecuentes'
      },
      {
        key: 'qr_multi_acceso',
        label: 'QR Multi-Acceso (Temporal)',
        description: 'Permite múltiples entradas/salidas en un tiempo definido'
      },
      {
        key: 'qr_contador_escaneos',
        label: 'QR para Reuniones (Contador)',
        description: 'Permite definir un límite de escaneos para un solo QR'
      }
    ]
  },
  finanzas: {
    label: 'Finanzas',
    icon: FaDollarSign,
    color: '#f59e0b',
    general: {
      key: 'finanzas',
      label: 'Pestaña Finanzas',
      description: 'Activar o desactivar toda la pestaña de Finanzas'
    },
    subcategorias: [
      {
        key: 'botones_prueba_admin',
        label: 'Botones de prueba para Administradores',
        description: 'Mostrar u ocultar los botones de prueba (Aplicar Cargos / Sanciones) en Finanzas'
      },
      {
        key: 'boton_ajuste_saldos_admin',
        label: 'Botón "Ajuste de Saldos" en Contacto',
        description: 'Mostrar u ocultar el botón "Ajuste de Saldos" en las tarjetas de contacto para Administradores'
      },
      {
        key: 'seleccion_meses_pago',
        label: 'Selección de meses y adeudos en pagos',
        description: 'Permite seleccionar meses específicos o cuotas pendientes al registrar pagos de residentes'
      }
    ]
  },
  administracion: {
    label: 'Administración',
    icon: FaCog,
    color: '#ef4444',
    general: {
      key: 'administracion',
      label: 'Pestaña Administración',
      description: 'Activar o desactivar toda la pestaña de Administración'
    },
    subcategorias: [
      {
        key: 'desactivacion_rfid_morosos',
        label: 'Desactivación RFID para Morosos',
        description: 'Permite desactivar accesos RFID de residentes morosos'
      },
      {
        key: 'consumo_agua_potable',
        label: 'Consumo-Agua Potable (CEA, JAPAM, etc.)',
        description: 'Permite gestionar el cobro por consumo variable de agua potable'
      },
      {
        key: 'revertir_consumo_agua',
        label: 'Revertir Saldos - Consumo Agua',
        description: 'Habilitar el botón de reversión de saldos/cargos de agua potable en la sección de administración'
      }
    ]
  },
  configuraciones: {
    label: 'Configuraciones',
    icon: FaCog,
    color: '#6366f1',
    general: {
      key: 'configuraciones',
      label: 'Pestaña Configuraciones',
      description: 'Activar o desactivar toda la pestaña de Configuraciones'
    },
    subcategorias: [
      {
        key: 'modulo_migracion_regularizacion',
        label: 'Migración/Regularización',
        description: 'Activar o desactivar la pestaña de Migración/Regularización'
      },
      {
        key: 'modulo_gestion_saldos',
        label: 'Gestión de Saldos',
        description: 'Activar o desactivar la tarjeta de Importar/Exportar saldos'
      },
      {
        key: 'modulo_importar_historico',
        label: 'Importación de Histórico',
        description: 'Activar o desactivar la tarjeta de Importación de histórico de estados de cuenta'
      }
    ]
  }
};

// Valores por defecto (todos activos)
const getDefaultModulos = () => {
  const defaults = {};
  Object.keys(categoriasModulos).forEach(categoriaKey => {
    const categoria = categoriasModulos[categoriaKey];
    defaults[categoria.general.key] = true;
    categoria.subcategorias.forEach(sub => {
      defaults[sub.key] = true;
    });
  });
  // Valores por defecto para la configuración de residentes
  defaults['residente_mostrar_sanciones'] = true;
  defaults['residente_mostrar_cuota_mensual'] = true;
  defaults['residente_mostrar_corte_dia'] = true;
  defaults['residente_mostrar_intereses'] = true;
  defaults['residente_mostrar_descuento_pronto_pago'] = true;
  defaults['residente_mostrar_cuotas_extras_cobro'] = true;
  defaults['residente_qr_simplificado'] = false;
  defaults['vigilancia_captura_imagenes'] = true;
  defaults['vigilancia_botones_whatsapp_qr'] = true;
  return defaults;
};

// Valores por defecto para la configuración física de paneles y puertas
const getDefaultPanelAccesosConfig = () => ({
  syncActive: true,
  panel1Activo: true,
  panel2Activo: true,
  panel4pActivo: true,
  panel1Nombre: 'Panel Principal (Vehicular)',
  panel2Nombre: 'Panel Cuarto Basura',
  panel4pNombre: 'Panel 4 Puertas (DS-K2814)',
  panel1_ip: '192.168.1.100',
  panel1_port: 8000,
  panel2_ip: '192.168.1.101',
  panel2_port: 8000,
  panel4p_ip: '192.168.1.118',
  panel4p_port: 8000,
  puertas: {
    p1_d1: { id: 1, panelId: 'panel_1', panelNombre: 'Panel 1 (DS-K2812)', nombre: 'Pluma Entrada', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta'] },
    p1_d2: { id: 2, panelId: 'panel_1', panelNombre: 'Panel 1 (DS-K2812)', nombre: 'Pluma Salida', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta'] },
    p2_d1: { id: 1, panelId: 'panel_2', panelNombre: 'Panel 2 (DS-K2811)', nombre: 'Puerta Basura', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta', 'residente'] },
    p4_d1: { id: 1, panelId: 'panel_4puertas', panelNombre: 'Panel 4 Puertas (DS-K2814)', nombre: 'Puerta 1 (Acceso)', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta'] },
    p4_d2: { id: 2, panelId: 'panel_4puertas', panelNombre: 'Panel 4 Puertas (DS-K2814)', nombre: 'Puerta 2 (Salida)', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta'] },
    p4_d3: { id: 3, panelId: 'panel_4puertas', panelNombre: 'Panel 4 Puertas (DS-K2814)', nombre: 'Puerta 3 (Acceso 2)', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta'] },
    p4_d4: { id: 4, panelId: 'panel_4puertas', panelNombre: 'Panel 4 Puertas (DS-K2814)', nombre: 'Puerta 4 (Salida 2)', accesoGeneral: false, rolesAutorizados: ['superadmin', 'admin', 'caseta'] }
  }
});

const GestionarModulosModal = ({ isOpen, onClose }) => {
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [selectedFracc, setSelectedFracc] = useState(null);
  const [modulos, setModulos] = useState(getDefaultModulos());
  const [modulosOriginales, setModulosOriginales] = useState(getDefaultModulos()); // Estado original para restaurar si se cancela
  const [panelAccesosConfig, setPanelAccesosConfig] = useState(getDefaultPanelAccesosConfig());
  const [panelAccesosConfigOriginal, setPanelAccesosConfigOriginal] = useState(getDefaultPanelAccesosConfig());
  const [loading, setLoading] = useState(false);
  const [savingLoading, setSavingLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [vistaResidentes, setVistaResidentes] = useState(null); // null | 'finanzas' | 'vigilancia' | 'control_accesos'

  const cargarConfiguracion = useCallback(async () => {
    if (!selectedFracc) return;

    try {
      console.log('🔵 [GestionarModulosModal] Cargando configuración para fraccionamiento:', selectedFracc);
      setLoading(true);
      const docRef = doc(db, 'fraccionamientos', selectedFracc);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const modulosHabilitados = data.modulosHabilitados || {};
        
        console.log('📦 [GestionarModulosModal] Datos de Firestore:', {
          modulosHabilitados,
          chat_comunitario: modulosHabilitados.chat_comunitario,
          comunidad: modulosHabilitados.comunidad,
          tipo_chat_comunitario: typeof modulosHabilitados.chat_comunitario,
          tipo_comunidad: typeof modulosHabilitados.comunidad
        });
        
        // Cargar módulos existentes o usar defaults
        const modulosActuales = getDefaultModulos();
        Object.keys(modulosActuales).forEach(key => {
          if (modulosHabilitados.hasOwnProperty(key)) {
            modulosActuales[key] = modulosHabilitados[key] === true;
          }
        });

        console.log('✅ [GestionarModulosModal] Módulos cargados en el modal:', {
          modulosActuales,
          chat_comunitario: modulosActuales.chat_comunitario,
          comunidad: modulosActuales.comunidad,
          tipo_chat_comunitario: typeof modulosActuales.chat_comunitario,
          tipo_comunidad: typeof modulosActuales.comunidad
        });

        setModulos(modulosActuales);
        setModulosOriginales(modulosActuales); // Guardar estado original
        setMessage(null);
      } else {
        console.log('🟡 [GestionarModulosModal] Documento no existe, usando defaults');
        // Si no existe, usar defaults
        const defaults = getDefaultModulos();
        setModulos(defaults);
        setModulosOriginales(defaults); // Guardar estado original
      }

      // Cargar configuración de control de accesos y paneles
      try {
        const panelDocRef = doc(db, 'fraccionamientos', selectedFracc, 'control_accesos_config', 'panel_config');
        const panelDocSnap = await getDoc(panelDocRef);
        if (panelDocSnap.exists()) {
          const pData = panelDocSnap.data();
          const pDefault = getDefaultPanelAccesosConfig();
          const pMerge = {
            ...pDefault,
            ...pData,
            syncActive: true,
            puertas: {
              ...pDefault.puertas,
              ...(pData.puertas || {})
            }
          };
          setPanelAccesosConfig(pMerge);
          setPanelAccesosConfigOriginal(pMerge);
        } else {
          const pDefault = getDefaultPanelAccesosConfig();
          setPanelAccesosConfig(pDefault);
          setPanelAccesosConfigOriginal(pDefault);
        }
      } catch (ePanel) {
        console.warn('⚠️ Error al cargar panel_config:', ePanel.message);
      }
    } catch (error) {
      console.error('❌ [GestionarModulosModal] Error cargando configuración:', error);
      setMessage({ type: 'error', text: 'Error al cargar la configuración' });
    } finally {
      setLoading(false);
    }
  }, [selectedFracc]);

  const cargarFraccionamientos = useCallback(async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'fraccionamientos'));
      const fraccs = snapshot.docs.map((doc) => ({
        id: doc.id,
        nombre: doc.data().nombre || doc.data().name || 'Sin nombre',
        activo: doc.data().activo === true || doc.data().estado === 'activo',
      }));
      setFraccionamientos(fraccs);

      // Asignar el primer fraccionamiento SOLO si no hay ninguno seleccionado previamente
      setSelectedFracc((prev) => {
        if (prev && fraccs.some((f) => f.id === prev)) {
          return prev;
        }
        return fraccs.length > 0 ? fraccs[0].id : null;
      });
    } catch (error) {
      console.error('Error cargando fraccionamientos:', error);
      setMessage({ type: 'error', text: 'Error al cargar fraccionamientos' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      cargarFraccionamientos();
      setMessage(null);
      setVistaResidentes(null);
    } else {
      setVistaResidentes(null);
    }
  }, [isOpen, cargarFraccionamientos]);

  useEffect(() => {
    if (selectedFracc && isOpen) {
      cargarConfiguracion();
    }
  }, [selectedFracc, isOpen, cargarConfiguracion]);

  const handleToggleModulo = (key) => {
    setModulos((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleToggleCategoria = (categoriaKey) => {
    const categoria = categoriasModulos[categoriaKey];
    const nuevoEstado = !modulos[categoria.general.key];
    
    setModulos((prev) => {
      const nuevo = { ...prev };
      // Toggle del general
      nuevo[categoria.general.key] = nuevoEstado;
      // Si se desactiva el general, desactivar también todas las subcategorías
      if (!nuevoEstado) {
        categoria.subcategorias.forEach(sub => {
          nuevo[sub.key] = false;
        });
      } else {
        // Si se activa el general, activar todas las subcategorías automáticamente
        categoria.subcategorias.forEach(sub => {
          nuevo[sub.key] = true;
        });
      }
      return nuevo;
    });
  };

  const handleGuardar = async () => {
    if (!selectedFracc) {
      setMessage({ type: 'error', text: 'Selecciona un fraccionamiento' });
      return;
    }
    await guardarConfiguracion();
  };

  const handleCancelar = () => {
    // Restaurar estado original antes de cerrar
    setModulos(modulosOriginales);
    setPanelAccesosConfig(panelAccesosConfigOriginal);
    setVistaResidentes(null);
    setMessage(null);
    onClose();
  };

  const guardarConfiguracion = async () => {
    try {
      setSavingLoading(true);
      const docRef = doc(db, 'fraccionamientos', selectedFracc);

      console.log('🔵 [GestionarModulosModal] Guardando configuración:', {
        fraccionamientoId: selectedFracc,
        modulosHabilitados: modulos,
        timestamp: new Date().toISOString()
      });

      await updateDoc(docRef, {
        modulosHabilitados: modulos,
        ultimaActualizacionModulos: new Date(),
      });

      // Guardar también la configuración de paneles y puertas en control_accesos_config/panel_config
      const panelConfigRef = doc(db, 'fraccionamientos', selectedFracc, 'control_accesos_config', 'panel_config');
      const puertasMapMiddleware = {
        puerta1: { 
          nombre: panelAccesosConfig.puertas?.p4_d1?.nombre || 'Puerta 1 (Acceso)', 
          accesoGeneral: Boolean(panelAccesosConfig.puertas?.p4_d1?.accesoGeneral) 
        },
        puerta2: { 
          nombre: panelAccesosConfig.puertas?.p4_d2?.nombre || 'Puerta 2 (Salida)', 
          accesoGeneral: Boolean(panelAccesosConfig.puertas?.p4_d2?.accesoGeneral) 
        },
        puerta3: { 
          nombre: panelAccesosConfig.puertas?.p4_d3?.nombre || 'Puerta 3 (Acceso 2)', 
          accesoGeneral: Boolean(panelAccesosConfig.puertas?.p4_d3?.accesoGeneral) 
        },
        puerta4: { 
          nombre: panelAccesosConfig.puertas?.p4_d4?.nombre || 'Puerta 4 (Salida 2)', 
          accesoGeneral: Boolean(panelAccesosConfig.puertas?.p4_d4?.accesoGeneral) 
        }
      };

      const puertasConNombres = { ...(panelAccesosConfig.puertas || {}) };
      Object.keys(puertasConNombres).forEach((k) => {
        const p = { ...puertasConNombres[k] };
        if (p.panelId === 'panel_1') p.panelNombre = panelAccesosConfig.panel1Nombre || 'Panel 1 (DS-K2812)';
        else if (p.panelId === 'panel_2') p.panelNombre = panelAccesosConfig.panel2Nombre || 'Panel 2 (DS-K2811)';
        else if (p.panelId === 'panel_4puertas') p.panelNombre = panelAccesosConfig.panel4pNombre || 'Panel 4 Puertas (DS-K2814)';
        puertasConNombres[k] = p;
      });

      await setDoc(panelConfigRef, {
        ...panelAccesosConfig,
        syncActive: true, // Blindaje de Acceso: Garantiza que los paneles se mantengan siempre conectados y activos
        puertas: puertasConNombres,
        puertasConfig: puertasMapMiddleware,
        ultimaActualizacion: new Date()
      }, { merge: true });

      console.log('✅ [GestionarModulosModal] Configuración de paneles guardada exitosamente en Firestore');

      // Actualizar estado original después de guardar exitosamente
      setModulosOriginales(modulos);
      setPanelAccesosConfigOriginal({
        ...panelAccesosConfig,
        syncActive: true
      });
      
      setMessage({
        type: 'success',
        text: '✅ Configuración de módulos guardada exitosamente',
      });
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      console.error('❌ [GestionarModulosModal] Error guardando configuración:', error);
      setMessage({
        type: 'error',
        text: 'Error al guardar la configuración: ' + error.message,
      });
    } finally {
      setSavingLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modulos-modal-overlay">
      <div className="modulos-modal-container">
        <div className="modulos-modal-header">
          {message && (
            <div className={`modulos-message modulos-message-top ${message.type}`}>
              {message.text}
            </div>
          )}
          <h2>Gestionar Módulos Visibles</h2>
          <p>Selecciona qué módulos estarán visibles para Administradores y Residentes</p>
          <button className="modulos-modal-close" onClick={handleCancelar}>
            <FaTimes />
          </button>
        </div>

        <div className="modulos-modal-content">
          {/* Selector de Fraccionamiento */}
          <div className="modulos-fracc-selector">
            <label>
              <FaBuilding /> Selecciona un Fraccionamiento:
            </label>
            <select
              value={selectedFracc || ''}
              onChange={(e) => setSelectedFracc(e.target.value)}
              disabled={loading}
            >
              <option value="">Cargando...</option>
              {fraccionamientos.map((fracc) => (
                <option key={fracc.id} value={fracc.id}>
                  {fracc.nombre} {fracc.activo ? '✓' : '(inactivo)'}
                </option>
              ))}
            </select>
          </div>

          {/* Grid de Categorías o Vista de Configuración para Residentes */}
          {loading ? (
            <div className="modulos-loading">Cargando módulos...</div>
          ) : vistaResidentes === 'finanzas' ? (
            <div className="modulos-residentes-container" style={{ padding: '8px 4px' }}>
              <div className="modulos-residentes-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                <button 
                  className="modulos-btn-back" 
                  onClick={() => setVistaResidentes(null)}
                  style={{ 
                    background: '#f1f5f9', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '6px',
                    padding: '6px 12px',
                    cursor: 'pointer', 
                    fontSize: '0.85rem', 
                    color: '#475569', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    fontWeight: 600
                  }}
                >
                  ← Volver a Categorías
                </button>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>Finanzas - Configuración de vista para Residentes</h3>
              </div>
              
              <div className="modulos-residentes-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Cuota Mensual */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Cuota Mensual</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el residente verá la minitarjeta de cuota mensual base en su configuración de cobros.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_mostrar_cuota_mensual'] === true}
                      onChange={() => handleToggleModulo('residente_mostrar_cuota_mensual')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Día de Corte */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Día de Corte</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el residente verá el día de corte mensual en su configuración de cobros.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_mostrar_corte_dia'] === true}
                      onChange={() => handleToggleModulo('residente_mostrar_corte_dia')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Intereses Moratorios */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Intereses Moratorios</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el residente verá las 3 minitarjetas de intereses moratorios (Inicio de Intereses, Estado de Intereses y Tasa de Interés) en su configuración de cobros.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_mostrar_intereses'] === true}
                      onChange={() => handleToggleModulo('residente_mostrar_intereses')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Descuento Pronto Pago */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Descuento Pronto Pago</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el residente verá la minitarjeta de descuento pronto pago en su configuración de cobros.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_mostrar_descuento_pronto_pago'] === true}
                      onChange={() => handleToggleModulo('residente_mostrar_descuento_pronto_pago')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Cuotas Extras */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Cuotas Extras (Cobros)</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el residente verá la minitarjeta de cuotas extraordinarias configuradas en la sección de cobros (esta opción no oculta la tarjeta del estado de cuenta superior).
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_mostrar_cuotas_extras_cobro'] === true}
                      onChange={() => handleToggleModulo('residente_mostrar_cuotas_extras_cobro')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Sanciones Administrativas */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Sanciones Administrativas</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el residente verá la minitarjeta de sanciones y su desglose detallado en el estado de cuenta y la configuración de cobros. Si está desactivado (OFF), se ocultará por completo.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_mostrar_sanciones'] === true}
                      onChange={() => handleToggleModulo('residente_mostrar_sanciones')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          ) : vistaResidentes === 'vigilancia' ? (
            <div className="modulos-residentes-container" style={{ padding: '8px 4px' }}>
              <div className="modulos-residentes-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                <button 
                  className="modulos-btn-back" 
                  onClick={() => setVistaResidentes(null)}
                  style={{ 
                    background: '#f1f5f9', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '6px',
                    padding: '6px 12px',
                    cursor: 'pointer', 
                    fontSize: '0.85rem', 
                    color: '#475569', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    fontWeight: 600
                  }}
                >
                  ← Volver a Categorías
                </button>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>Vigilancia - Configuración de vista para Residentes</h3>
              </div>
              
              <div className="modulos-residentes-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Formulario QR de Invitado Simplificado */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Formulario QR de Invitado Simplificado</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el formulario para generar QR de invitado solicitará únicamente el nombre, día de visita y observaciones. Oculta teléfono, placas, número de visitantes y medio de transporte.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['residente_qr_simplificado'] === true}
                      onChange={() => handleToggleModulo('residente_qr_simplificado')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Habilitar Captura de Imágenes de Seguridad */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Habilitar Captura de Imágenes de Seguridad</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), el vigilante deberá fotografiar la identificación (INE), placas y cajuela al registrar ingresos. Si está desactivado (OFF), se saltará la toma de fotos, agilizando el acceso.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['vigilancia_captura_imagenes'] === true}
                      onChange={() => handleToggleModulo('vigilancia_captura_imagenes')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>

                {/* Habilitar Botones de Envío y Compartir QR por WhatsApp */}
                <div className="modulos-subcategoria-item" style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '10px', 
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}>
                  <div className="modulos-subcategoria-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '16px' }}>
                    <span className="modulos-subcategoria-label" style={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem' }}>Botones de Envío y Compartir QR por WhatsApp</span>
                    <span className="modulos-subcategoria-desc" style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4' }}>
                      Si está activado (ON), muestra los botones 'Enviar a mi WhatsApp' y 'Compartir con invitado' en el modal de generación de código QR. Si está desactivado (OFF), se ocultan estos botones para fraccionamientos sin contratación de la suite de WhatsApp.
                    </span>
                  </div>
                  <label className="modulos-toggle" style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={modulos['vigilancia_botones_whatsapp_qr'] !== false}
                      onChange={() => handleToggleModulo('vigilancia_botones_whatsapp_qr')}
                    />
                    <span className="modulos-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          ) : vistaResidentes === 'control_accesos' ? (
            <div className="modulos-residentes-container" style={{ padding: '8px 4px' }}>
              <div className="modulos-residentes-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                <button 
                  className="modulos-btn-back" 
                  onClick={() => setVistaResidentes(null)}
                  style={{ 
                    background: '#f1f5f9', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '6px', 
                    padding: '6px 12px', 
                    cursor: 'pointer', 
                    fontSize: '0.85rem', 
                    color: '#475569', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px', 
                    fontWeight: 600 
                  }}
                >
                  ← Volver a Categorías
                </button>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>Control de Acceso - Configuración de Paneles y Emergencias</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Activa paneles, configura bypass de morosidad (acceso general) y permisos de apertura de emergencia.</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* 1. Activar Paneles de Acceso */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaMicrochip style={{ color: '#0288d1' }} /> 1. Paneles de Acceso Activos (Controladores Físicos)
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                    {/* Panel 1 (DS-K2812 - Azul) */}
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#1d4ed8', background: '#dbeafe', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
                            Panel 1 (Azul)
                          </span>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>2 Puertas / 4 Lectores (DS-K2812)</div>
                        </div>
                        <label className="modulos-toggle modulos-toggle-small" title="Activar / Desactivar Panel 1">
                          <input
                            type="checkbox"
                            checked={panelAccesosConfig.panel1Activo !== false}
                            onChange={() => setPanelAccesosConfig(prev => ({ ...prev, panel1Activo: !prev.panel1Activo }))}
                          />
                          <span className="modulos-toggle-slider"></span>
                        </label>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                          Nombre del Panel:
                        </label>
                        <input
                          type="text"
                          value={panelAccesosConfig.panel1Nombre ?? 'Hikvision DS-K2812'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPanelAccesosConfig(prev => ({ ...prev, panel1Nombre: val }));
                          }}
                          placeholder="Ej: Panel Principal (Vehicular)"
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            fontSize: '0.84rem',
                            fontWeight: 600,
                            color: '#1e3a8a',
                            border: '1px solid #93c5fd',
                            borderRadius: '6px',
                            background: '#fff',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* Panel 2 (DS-K2811 - Púrpura) */}
                    <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
                            Panel 2 (Púrpura)
                          </span>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>1 Puerta / 2 Lectores (DS-K2811)</div>
                        </div>
                        <label className="modulos-toggle modulos-toggle-small" title="Activar / Desactivar Panel 2">
                          <input
                            type="checkbox"
                            checked={panelAccesosConfig.panel2Activo !== false}
                            onChange={() => setPanelAccesosConfig(prev => ({ ...prev, panel2Activo: !prev.panel2Activo }))}
                          />
                          <span className="modulos-toggle-slider"></span>
                        </label>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                          Nombre del Panel:
                        </label>
                        <input
                          type="text"
                          value={panelAccesosConfig.panel2Nombre ?? 'Hikvision DS-K2811'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPanelAccesosConfig(prev => ({ ...prev, panel2Nombre: val }));
                          }}
                          placeholder="Ej: Panel Cuarto Basura"
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            fontSize: '0.84rem',
                            fontWeight: 600,
                            color: '#581c87',
                            border: '1px solid #c084fc',
                            borderRadius: '6px',
                            background: '#fff',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* Panel 3 (DS-K2814 - Verde Esmeralda) */}
                    <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#059669', background: '#d1fae5', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
                            Panel 3 (Verde Esmeralda)
                          </span>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px' }}>4 Puertas / 8 Lectores (DS-K2814)</div>
                        </div>
                        <label className="modulos-toggle modulos-toggle-small" title="Activar / Desactivar Panel 3">
                          <input
                            type="checkbox"
                            checked={panelAccesosConfig.panel4pActivo !== false}
                            onChange={() => setPanelAccesosConfig(prev => ({ ...prev, panel4pActivo: !prev.panel4pActivo }))}
                          />
                          <span className="modulos-toggle-slider"></span>
                        </label>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                          Nombre del Panel:
                        </label>
                        <input
                          type="text"
                          value={panelAccesosConfig.panel4pNombre ?? 'Hikvision DS-K2814'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPanelAccesosConfig(prev => ({ ...prev, panel4pNombre: val }));
                          }}
                          placeholder="Ej: Panel 4 Puertas (DS-K2814)"
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            fontSize: '0.84rem',
                            fontWeight: 600,
                            color: '#064e3b',
                            border: '1px solid #6ee7b7',
                            borderRadius: '6px',
                            background: '#fff',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Configuración Detallada por Puerta: Nombre, Bypass de Morosidad y Roles */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaDoorOpen style={{ color: '#16a34a' }} /> 2. Puertas, Acceso General (Bypass) y Botones de Emergencia
                  </h4>
                  <p style={{ margin: '0 0 16px', fontSize: '0.78rem', color: '#64748b' }}>
                    Configura el nombre de cada puerta, si concede paso libre sin importar adeudos del residente, y qué roles podrán presionar el botón de apertura rápida en Vigilancia.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {Object.entries(panelAccesosConfig.puertas || {}).map(([pKey, pVal]) => {
                      const panelActivo = (pVal.panelId === 'panel_1' && panelAccesosConfig.panel1Activo !== false) ||
                                          (pVal.panelId === 'panel_2' && panelAccesosConfig.panel2Activo !== false) ||
                                          (pVal.panelId === 'panel_4puertas' && panelAccesosConfig.panel4pActivo !== false);
                      if (!panelActivo) return null;

                      const roles = pVal.rolesAutorizados || ['superadmin', 'admin', 'caseta'];

                      const toggleRole = (rolKey) => {
                        let nuevoRoles = [];
                        if (rolKey === 'admin') {
                          const tieneAdmin = roles.includes('admin') || roles.includes('superadmin');
                          if (tieneAdmin) {
                            nuevoRoles = roles.filter(r => r !== 'admin' && r !== 'superadmin');
                          } else {
                            nuevoRoles = [...roles.filter(r => r !== 'admin' && r !== 'superadmin'), 'admin', 'superadmin'];
                          }
                        } else {
                          nuevoRoles = roles.includes(rolKey) 
                            ? roles.filter(r => r !== rolKey) 
                            : [...roles, rolKey];
                        }
                        setPanelAccesosConfig(prev => ({
                          ...prev,
                          puertas: {
                            ...prev.puertas,
                            [pKey]: {
                              ...prev.puertas[pKey],
                              rolesAutorizados: nuevoRoles
                            }
                          }
                        }));
                      };

                      return (
                        <div 
                          key={pKey} 
                          style={{ 
                            background: '#fff', 
                            border: pVal.accesoGeneral ? '1px solid #86efac' : '1px solid #e2e8f0', 
                            borderRadius: '8px', 
                            padding: '14px',
                            boxShadow: pVal.accesoGeneral ? '0 1px 3px rgba(34, 197, 94, 0.08)' : 'none'
                          }}
                        >
                          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px' }}>
                              {(() => {
                                const esP1 = pVal.panelId === 'panel_1' || pVal.panelId === 'panel1';
                                const esP2 = pVal.panelId === 'panel_2' || pVal.panelId === 'panel2';
                                const badgeColor = esP1 ? '#1d4ed8' : (esP2 ? '#7c3aed' : '#059669');
                                const badgeBg = esP1 ? '#eff6ff' : (esP2 ? '#faf5ff' : '#ecfdf5');
                                const badgeBorder = esP1 ? '#bfdbfe' : (esP2 ? '#e9d5ff' : '#a7f3d0');
                                const badgeText = esP1 
                                  ? (panelAccesosConfig.panel1Nombre || 'Panel 1 (DS-K2812)') 
                                  : (esP2 
                                      ? (panelAccesosConfig.panel2Nombre || 'Panel 2 (DS-K2811)') 
                                      : (panelAccesosConfig.panel4pNombre || 'Panel 3 (DS-K2814)'));
                                return (
                                  <span style={{ 
                                    fontSize: '0.74rem', 
                                    padding: '3px 8px', 
                                    borderRadius: '5px', 
                                    background: badgeBg, 
                                    color: badgeColor, 
                                    border: `1px solid ${badgeBorder}`, 
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {badgeText}
                                  </span>
                                );
                              })()}
                              <input
                                type="text"
                                value={pVal.nombre}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setPanelAccesosConfig(prev => ({
                                    ...prev,
                                    puertas: {
                                      ...prev.puertas,
                                      [pKey]: { ...prev.puertas[pKey], nombre: val }
                                    }
                                  }));
                                }}
                                placeholder="Nombre de la puerta"
                                style={{
                                  padding: '6px 10px',
                                  fontSize: '0.88rem',
                                  fontWeight: 600,
                                  color: '#0f172a',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  flex: 1,
                                  minWidth: '150px'
                                }}
                              />
                            </div>

                            {/* Mini Toggle Acceso General */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: pVal.accesoGeneral ? '#15803d' : '#475569' }}>
                                  {pVal.accesoGeneral ? '⭐ Acceso General ACTIVO' : 'Paso Restringido a Morosos'}
                                </div>
                                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                  {pVal.accesoGeneral ? 'Paso libre sin importar adeudos' : 'Bloquea tags con adeudo'}
                                </div>
                              </div>
                              <label className="modulos-toggle modulos-toggle-small" style={{ margin: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={pVal.accesoGeneral === true}
                                  onChange={() => {
                                    setPanelAccesosConfig(prev => ({
                                      ...prev,
                                      puertas: {
                                        ...prev.puertas,
                                        [pKey]: { ...prev.puertas[pKey], accesoGeneral: !prev.puertas[pKey].accesoGeneral }
                                      }
                                    }));
                                  }}
                                />
                                <span className="modulos-toggle-slider"></span>
                              </label>
                            </div>
                          </div>

                          {/* Selector de Roles para el Botón de Emergencia en Vigilancia */}
                          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                              🚨 Mostrar botón de apertura en Vigilancia a:
                            </span>
                            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#334155', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={roles.includes('superadmin') || roles.includes('admin')}
                                  onChange={() => toggleRole('admin')}
                                />
                                Administrador
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#334155', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={roles.includes('caseta')}
                                  onChange={() => toggleRole('caseta')}
                                />
                                Caseta de Vigilancia
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#334155', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={roles.includes('residente')}
                                  onChange={() => toggleRole('residente')}
                                />
                                Residentes
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="modulos-categorias-grid">
              {Object.entries(categoriasModulos).map(([categoriaKey, categoria]) => {
                const IconComponent = categoria.icon;
                const isGeneralActive = modulos[categoria.general.key] === true;
                
                return (
                  <div key={categoriaKey} className="modulos-categoria-card">
                    <div className="modulos-categoria-header">
                      <div className="modulos-categoria-icon" style={{ backgroundColor: `${categoria.color}15`, color: categoria.color }}>
                        <IconComponent />
                      </div>
                      <div className="modulos-categoria-title">
                        <h3>{categoria.label}</h3>
                      </div>
                      <label className="modulos-toggle">
                        <input
                          type="checkbox"
                          checked={isGeneralActive}
                          onChange={() => handleToggleCategoria(categoriaKey)}
                        />
                        <span className="modulos-toggle-slider"></span>
                      </label>
                    </div>

                    <div className="modulos-categoria-body">
                      {/* Descripción de la categoría */}
                      <div className="modulos-categoria-descripcion">
                        <span className="modulos-subcategoria-desc">{categoria.general.description}</span>
                      </div>

                      {/* Subcategorías */}
                      {categoria.subcategorias.length > 0 && (
                        <div className="modulos-subcategorias">
                          {categoria.subcategorias.map((sub) => (
                            <div key={sub.key} className="modulos-subcategoria-item">
                              <div className="modulos-subcategoria-info">
                                <span className="modulos-subcategoria-label">{sub.label}</span>
                                <span className="modulos-subcategoria-desc">{sub.description}</span>
                              </div>
                              <label className="modulos-toggle modulos-toggle-small">
                                <input
                                  type="checkbox"
                                  checked={modulos[sub.key] === true && isGeneralActive}
                                  onChange={() => handleToggleModulo(sub.key)}
                                  disabled={!isGeneralActive}
                                />
                                <span className="modulos-toggle-slider"></span>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Botón de configuración para residentes (Especial de Finanzas) */}
                      {categoriaKey === 'finanzas' && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                          <button
                            type="button"
                            className="modulos-btn-config-residentes"
                            onClick={() => setVistaResidentes('finanzas')}
                            style={{ 
                              width: '100%', 
                              padding: '8px 12px', 
                              backgroundColor: '#f59e0b10', 
                              color: '#d97706', 
                              border: '1px dashed #f59e0b50', 
                              borderRadius: '6px', 
                              fontWeight: 600, 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s'
                            }}
                          >
                            ⚙️ Configurar vista Residentes
                          </button>
                        </div>
                      )}

                      {/* Botón de configuración para residentes (Especial de Vigilancia) */}
                      {categoriaKey === 'vigilancia' && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                          <button
                            type="button"
                            className="modulos-btn-config-residentes"
                            onClick={() => setVistaResidentes('vigilancia')}
                            style={{ 
                              width: '100%', 
                              padding: '8px 12px', 
                              backgroundColor: '#6366f110', 
                              color: '#4f46e5', 
                              border: '1px dashed #6366f150', 
                              borderRadius: '6px', 
                              fontWeight: 600, 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s'
                            }}
                          >
                            ⚙️ Configurar vista Residentes
                          </button>
                        </div>
                      )}

                      {/* Botón de configuración de Paneles y Accesos de Emergencia (Especial de Administración) */}
                      {categoriaKey === 'administracion' && (modulos['desactivacion_rfid_morosos'] === true || isGeneralActive) && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                          <button
                            type="button"
                            className="modulos-btn-config-residentes"
                            onClick={() => setVistaResidentes('control_accesos')}
                            style={{ 
                              width: '100%', 
                              padding: '8px 12px', 
                              backgroundColor: '#ef444410', 
                              color: '#dc2626', 
                              border: '1px dashed #ef444450', 
                              borderRadius: '6px', 
                              fontWeight: 600, 
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontSize: '0.85rem',
                              transition: 'all 0.2s'
                            }}
                          >
                            ⚙️ Configurar Paneles y Accesos de Emergencia
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="modulos-categoria-status">
                      {isGeneralActive ? (
                        <span className="status-enabled">
                          <FaCheck /> Habilitado
                        </span>
                      ) : (
                        <span className="status-disabled">
                          <FaExclamationTriangle /> Deshabilitado
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modulos-modal-footer">
          <button
            className="modulos-btn-cancel"
            onClick={handleCancelar}
            disabled={savingLoading}
          >
            Cancelar
          </button>
          <button
            className="modulos-btn-save"
            onClick={handleGuardar}
            disabled={savingLoading || loading || !selectedFracc}
          >
            {savingLoading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GestionarModulosModal;
